import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import upazilas from '../data/bd-upazilas.json';
import { register as registerRequest } from '../api/auth.api';
import { setRegistrationPhone } from '../utils/registration';

const divisions = [
  'Barisal',
  'Chattogram',
  'Dhaka',
  'Khulna',
  'Mymensingh',
  'Rajshahi',
  'Rangpur',
  'Sylhet',
];

const districts = [
  { name: 'Bagerhat', division: 'Khulna' },
  { name: 'Bandarban', division: 'Chattogram' },
  { name: 'Barguna', division: 'Barisal' },
  { name: 'Barisal', division: 'Barisal' },
  { name: 'Bhola', division: 'Barisal' },
  { name: 'Bogra', division: 'Rajshahi' },
  { name: 'Brahmanbaria', division: 'Chattogram' },
  { name: 'Chandpur', division: 'Chattogram' },
  { name: 'Chapai Nawabganj', division: 'Rajshahi' },
  { name: 'Chattogram', division: 'Chattogram' },
  { name: 'Chuadanga', division: 'Khulna' },
  { name: 'Comilla', division: 'Chattogram' },
  { name: "Cox's Bazar", division: 'Chattogram' },
  { name: 'Dhaka', division: 'Dhaka' },
  { name: 'Dinajpur', division: 'Rangpur' },
  { name: 'Faridpur', division: 'Dhaka' },
  { name: 'Feni', division: 'Chattogram' },
  { name: 'Gaibandha', division: 'Rangpur' },
  { name: 'Gazipur', division: 'Dhaka' },
  { name: 'Gopalganj', division: 'Dhaka' },
  { name: 'Habiganj', division: 'Sylhet' },
  { name: 'Jamalpur', division: 'Mymensingh' },
  { name: 'Jashore', division: 'Khulna' },
  { name: 'Jhalokati', division: 'Barisal' },
  { name: 'Jhenaidah', division: 'Khulna' },
  { name: 'Joypurhat', division: 'Rajshahi' },
  { name: 'Khagrachari', division: 'Chattogram' },
  { name: 'Khulna', division: 'Khulna' },
  { name: 'Kishoreganj', division: 'Dhaka' },
  { name: 'Kurigram', division: 'Rangpur' },
  { name: 'Kushtia', division: 'Khulna' },
  { name: 'Lakshmipur', division: 'Chattogram' },
  { name: 'Lalmonirhat', division: 'Rangpur' },
  { name: 'Madaripur', division: 'Dhaka' },
  { name: 'Magura', division: 'Khulna' },
  { name: 'Manikganj', division: 'Dhaka' },
  { name: 'Meherpur', division: 'Khulna' },
  { name: 'Moulvibazar', division: 'Sylhet' },
  { name: 'Munshiganj', division: 'Dhaka' },
  { name: 'Mymensingh', division: 'Mymensingh' },
  { name: 'Naogaon', division: 'Rajshahi' },
  { name: 'Narail', division: 'Khulna' },
  { name: 'Narayanganj', division: 'Dhaka' },
  { name: 'Narsingdi', division: 'Dhaka' },
  { name: 'Natore', division: 'Rajshahi' },
  { name: 'Netrokona', division: 'Mymensingh' },
  { name: 'Nilphamari', division: 'Rangpur' },
  { name: 'Noakhali', division: 'Chattogram' },
  { name: 'Pabna', division: 'Rajshahi' },
  { name: 'Panchagarh', division: 'Rangpur' },
  { name: 'Patuakhali', division: 'Barisal' },
  { name: 'Pirojpur', division: 'Barisal' },
  { name: 'Rajbari', division: 'Dhaka' },
  { name: 'Rajshahi', division: 'Rajshahi' },
  { name: 'Rangamati', division: 'Chattogram' },
  { name: 'Rangpur', division: 'Rangpur' },
  { name: 'Satkhira', division: 'Khulna' },
  { name: 'Shariatpur', division: 'Dhaka' },
  { name: 'Sherpur', division: 'Mymensingh' },
  { name: 'Sirajganj', division: 'Rajshahi' },
  { name: 'Sunamganj', division: 'Sylhet' },
  { name: 'Sylhet', division: 'Sylhet' },
  { name: 'Tangail', division: 'Dhaka' },
  { name: 'Thakurgaon', division: 'Rangpur' },
];

const roles = ['Batsman', 'Bowler', 'All-Rounder', 'Wicket-Keeper'];
const battingStyles = ['Right Hand', 'Left Hand'];
const bowlingStyles = [
  'Right Arm Fast',
  'Right Arm Medium',
  'Left Arm Fast',
  'Left Arm Spin',
  'Right Arm Spin',
];
const genderOptions = ['Male', 'Female', 'Other'];

type UpazilaEntry = {
  upazila: string;
  district: string;
  division: string;
};

type RegisterForm = {
  fullName: string;
  gender: string;
  fatherName: string;
  motherName: string;
  phone: string;
  whatsappNumber: string;
  email: string;
  dob: string;
  division: string;
  district: string;
  upazilla: string;
  playingRole: string;
  battingStyle: string;
  bowlingStyle: string;
  jerseySize: string;
  nid?: string;
  profilePhoto: File | null;
  password: string;
  confirmPassword: string;
};

type FieldErrors = Partial<Record<keyof RegisterForm, string>>;

const isEmail = (value: string) => /.+@.+\..+/.test(value);

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [registerLoading, setRegisterLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [registerError, setRegisterError] = useState('');
  const [registerForm, setRegisterForm] = useState<RegisterForm>({
    fullName: '',
    gender: '',
    fatherName: '',
    motherName: '',
    phone: '',
    whatsappNumber: '',
    email: '',
    dob: '',
    division: '',
    district: '',
    upazilla: '',
    playingRole: '',
    battingStyle: '',
    bowlingStyle: '',
    jerseySize: '',
    nid: '',
    profilePhoto: null,
    password: '',
    confirmPassword: '',
  });

  const upazilaData = upazilas as UpazilaEntry[];

  const requiresBowlingStyle = useMemo(
    () => ['Bowler', 'All-Rounder'].includes(registerForm.playingRole),
    [registerForm.playingRole]
  );

  const handleRegisterChange = (field: keyof RegisterForm, value: string | File | null) => {
    setRegisterForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      // Validate file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setFieldErrors((prev) => ({
          ...prev,
          profilePhoto: 'File size exceeds 5MB limit. Please choose a smaller file.',
        }));
        event.target.value = ''; // Clear the input
        handleRegisterChange('profilePhoto', null);
        return;
      }
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setFieldErrors((prev) => ({
          ...prev,
          profilePhoto: 'Invalid file type. Please upload JPEG, PNG, GIF, or WEBP image.',
        }));
        event.target.value = ''; // Clear the input
        handleRegisterChange('profilePhoto', null);
        return;
      }
      // Clear any previous errors
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.profilePhoto;
        return newErrors;
      });
    }
    handleRegisterChange('profilePhoto', file);
  };

  const handleDivisionChange = (value: string) => {
    setRegisterForm((prev) => ({
      ...prev,
      division: value,
      district: '',
      upazilla: '',
    }));
  };

  const handleDistrictChange = (value: string) => {
    setRegisterForm((prev) => ({
      ...prev,
      district: value,
      upazilla: '',
    }));
  };

  const handleWhatsappSame = (checked: boolean) => {
    setRegisterForm((prev) => ({
      ...prev,
      whatsappNumber: checked ? prev.phone : prev.whatsappNumber,
    }));
  };

  const availableDistricts = useMemo(() => {
    if (!registerForm.division) {
      return [];
    }
    return districts.filter((district) => district.division === registerForm.division);
  }, [registerForm.division]);

  const availableUpazilas = useMemo(() => {
    if (!registerForm.district) {
      return [];
    }
    // Find district name from districts array
    const selectedDistrict = districts.find(d => d.name === registerForm.district);
    if (!selectedDistrict) {
      return [];
    }
    return upazilaData.filter((upazila) => upazila.district === selectedDistrict.name);
  }, [registerForm.district, upazilaData]);

  const validateRegister = () => {
    const errors: FieldErrors = {};
    if (!registerForm.fullName.trim()) {
      errors.fullName = 'Full name is required.';
    }
    if (!registerForm.gender) {
      errors.gender = 'Gender is required.';
    }
    if (!registerForm.fatherName.trim()) {
      errors.fatherName = "Father's name is required.";
    }
    if (!registerForm.motherName.trim()) {
      errors.motherName = "Mother's name is required.";
    }
    if (!registerForm.phone.trim()) {
      errors.phone = 'Phone number is required.';
    }
    if (!registerForm.whatsappNumber.trim()) {
      errors.whatsappNumber = 'WhatsApp number is required.';
    }
    if (registerForm.email && !isEmail(registerForm.email)) {
      errors.email = 'Enter a valid email.';
    }
    if (!registerForm.dob) {
      errors.dob = 'Date of birth is required.';
    }
    if (!registerForm.division) {
      errors.division = 'Division is required.';
    }
    if (!registerForm.district) {
      errors.district = 'District is required.';
    }
    if (!registerForm.upazilla.trim()) {
      errors.upazilla = 'Upazilla is required.';
    }
    if (!registerForm.playingRole) {
      errors.playingRole = 'Playing role is required.';
    }
    if (!registerForm.battingStyle) {
      errors.battingStyle = 'Batting style is required.';
    }
    if (requiresBowlingStyle && !registerForm.bowlingStyle) {
      errors.bowlingStyle = 'Bowling style is required.';
    }
    if (!registerForm.profilePhoto) {
      errors.profilePhoto = 'Profile photo is required.';
    }
    if (registerForm.password.length < 6) {
      errors.password = 'Password must be at least 6 characters.';
    }
    if (registerForm.password !== registerForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRegisterSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateRegister()) {
      return;
    }

    setRegisterLoading(true);
    setRegisterError('');
    try {
      await registerRequest(
        {
          full_name: registerForm.fullName,
          gender: registerForm.gender,
          father_name: registerForm.fatherName,
          mother_name: registerForm.motherName,
          phone: registerForm.phone,
          whatsapp_number: registerForm.whatsappNumber,
          email: registerForm.email || undefined,
          dob: registerForm.dob,
          division: registerForm.division,
          district: registerForm.district,
          upazilla: registerForm.upazilla,
          playing_role: registerForm.playingRole,
          batting_style: registerForm.battingStyle,
          bowling_style: requiresBowlingStyle ? registerForm.bowlingStyle : undefined,
          jersey_size: registerForm.jerseySize || undefined,
          nid: registerForm.nid || undefined,
          password: registerForm.password,
        },
        registerForm.profilePhoto || undefined
      );
      setRegistrationPhone(registerForm.phone);
      navigate('/payment');
    } catch (err: any) {
      console.error('Registration error:', err);
      
      // Handle different types of errors
      if (err.response) {
        // Server responded with error status
        const errorData = err.response.data;
        if (errorData?.message) {
          setRegisterError(errorData.message);
        } else if (errorData?.error) {
          setRegisterError(errorData.error);
        } else if (err.response.status === 400) {
          setRegisterError('Invalid registration data. Please check all fields.');
        } else if (err.response.status === 409) {
          setRegisterError('Phone number or email already registered.');
        } else if (err.response.status === 413) {
          setRegisterError('Profile photo is too large. Maximum size is 5MB.');
        } else if (err.response.status >= 500) {
          setRegisterError('Server error. Please try again later.');
        } else {
          setRegisterError(`Registration failed: ${err.response.statusText || 'Unknown error'}`);
        }
      } else if (err.request) {
        // Request was made but no response received
        if (err.code === 'ECONNABORTED') {
          setRegisterError('Request timeout. Please check your connection and try again.');
        } else {
          setRegisterError('Unable to connect to server. Please check your internet connection.');
        }
      } else {
        // Something else happened
        setRegisterError(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card card-surface shadow-sm">
            <div className="card-body p-4">
              <h1 className="h4 mb-3 text-center">Player Registration</h1>
              <form onSubmit={handleRegisterSubmit} className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">
                    Full Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${fieldErrors.fullName ? 'is-invalid' : ''}`}
                    value={registerForm.fullName}
                    onChange={(event) => handleRegisterChange('fullName', event.target.value)}
                  />
                  {fieldErrors.fullName && (
                    <div className="invalid-feedback">{fieldErrors.fullName}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Gender <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${fieldErrors.gender ? 'is-invalid' : ''}`}
                    value={registerForm.gender}
                    onChange={(event) => handleRegisterChange('gender', event.target.value)}
                  >
                    <option value="">Select gender</option>
                    {genderOptions.map((gender) => (
                      <option key={gender} value={gender}>
                        {gender}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.gender && (
                    <div className="invalid-feedback">{fieldErrors.gender}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Father's Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${fieldErrors.fatherName ? 'is-invalid' : ''}`}
                    value={registerForm.fatherName}
                    onChange={(event) => handleRegisterChange('fatherName', event.target.value)}
                  />
                  {fieldErrors.fatherName && (
                    <div className="invalid-feedback">{fieldErrors.fatherName}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Mother's Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${fieldErrors.motherName ? 'is-invalid' : ''}`}
                    value={registerForm.motherName}
                    onChange={(event) => handleRegisterChange('motherName', event.target.value)}
                  />
                  {fieldErrors.motherName && (
                    <div className="invalid-feedback">{fieldErrors.motherName}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Phone Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className={`form-control ${fieldErrors.phone ? 'is-invalid' : ''}`}
                    value={registerForm.phone}
                    onChange={(event) => handleRegisterChange('phone', event.target.value)}
                  />
                  <div className="form-text">Used for OTP verification.</div>
                  {fieldErrors.phone && (
                    <div className="invalid-feedback">{fieldErrors.phone}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    WhatsApp Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className={`form-control ${fieldErrors.whatsappNumber ? 'is-invalid' : ''}`}
                    value={registerForm.whatsappNumber}
                    onChange={(event) => handleRegisterChange('whatsappNumber', event.target.value)}
                  />
                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="whatsappSame"
                      checked={
                        Boolean(registerForm.phone) &&
                        registerForm.whatsappNumber === registerForm.phone
                      }
                      onChange={(event) => handleWhatsappSame(event.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="whatsappSame">
                      <span className="text-white bg-dark px-2 py-1 rounded">Same as Mobile</span>
                    </label>
                  </div>
                  {fieldErrors.whatsappNumber && (
                    <div className="invalid-feedback">{fieldErrors.whatsappNumber}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Email (optional)</label>
                  <input
                    type="email"
                    className={`form-control ${fieldErrors.email ? 'is-invalid' : ''}`}
                    value={registerForm.email}
                    onChange={(event) => handleRegisterChange('email', event.target.value)}
                  />
                  {fieldErrors.email && <div className="invalid-feedback">{fieldErrors.email}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Date of Birth <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${fieldErrors.dob ? 'is-invalid' : ''}`}
                    value={registerForm.dob}
                    onChange={(event) => handleRegisterChange('dob', event.target.value)}
                  />
                  {fieldErrors.dob && <div className="invalid-feedback">{fieldErrors.dob}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Division <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${fieldErrors.division ? 'is-invalid' : ''}`}
                    value={registerForm.division}
                    onChange={(event) => handleDivisionChange(event.target.value)}
                  >
                    <option value="">Select division</option>
                    {divisions.map((division) => (
                      <option key={division} value={division}>
                        {division}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.division && (
                    <div className="invalid-feedback">{fieldErrors.division}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    District <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${fieldErrors.district ? 'is-invalid' : ''}`}
                    value={registerForm.district}
                    onChange={(event) => handleDistrictChange(event.target.value)}
                    disabled={!registerForm.division}
                  >
                    <option value="">
                      {registerForm.division ? 'Select district' : 'Select division first'}
                    </option>
                    {availableDistricts.map((district) => (
                      <option key={district.name} value={district.name}>
                        {district.name}
                      </option>
                    ))}
                  </select>
                  {!registerForm.division && (
                    <div className="form-text text-info mt-2">
                      <i className="bi bi-info-circle" />
                      <span>Please select a division first</span>
                    </div>
                  )}
                  {fieldErrors.district && (
                    <div className="invalid-feedback">{fieldErrors.district}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Upazilla <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${fieldErrors.upazilla ? 'is-invalid' : ''}`}
                    value={registerForm.upazilla}
                    onChange={(event) => handleRegisterChange('upazilla', event.target.value)}
                    disabled={!registerForm.district}
                  >
                    <option value="">
                      {registerForm.district ? 'Select upazilla' : 'Select district first'}
                    </option>
                    {availableUpazilas.map((upazila) => (
                      <option key={upazila.upazila} value={upazila.upazila}>
                        {upazila.upazila}
                      </option>
                    ))}
                  </select>
                  {!registerForm.district && (
                    <div className="form-text text-info mt-2">
                      <i className="bi bi-info-circle" />
                      <span>Please select a district first</span>
                    </div>
                  )}
                  {fieldErrors.upazilla && (
                    <div className="invalid-feedback">{fieldErrors.upazilla}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Playing Role <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${fieldErrors.playingRole ? 'is-invalid' : ''}`}
                    value={registerForm.playingRole}
                    onChange={(event) => handleRegisterChange('playingRole', event.target.value)}
                  >
                    <option value="">Select role</option>
                    {roles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.playingRole && (
                    <div className="invalid-feedback">{fieldErrors.playingRole}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Batting Style <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${fieldErrors.battingStyle ? 'is-invalid' : ''}`}
                    value={registerForm.battingStyle}
                    onChange={(event) => handleRegisterChange('battingStyle', event.target.value)}
                  >
                    <option value="">Select style</option>
                    {battingStyles.map((style) => (
                      <option key={style} value={style}>
                        {style}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.battingStyle && (
                    <div className="invalid-feedback">{fieldErrors.battingStyle}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Bowling Style {requiresBowlingStyle && <span className="text-danger">*</span>}
                  </label>
                  <select
                    className={`form-select ${fieldErrors.bowlingStyle ? 'is-invalid' : ''}`}
                    value={registerForm.bowlingStyle}
                    onChange={(event) => handleRegisterChange('bowlingStyle', event.target.value)}
                    disabled={!requiresBowlingStyle}
                  >
                    <option value="">
                      {requiresBowlingStyle ? 'Select style' : 'Select playing role first'}
                    </option>
                    {bowlingStyles.map((style) => (
                      <option key={style} value={style}>
                        {style}
                      </option>
                    ))}
                  </select>
                  {!requiresBowlingStyle && (
                    <div className="form-text text-info mt-2">
                      <i className="bi bi-info-circle" />
                      <span>Select "Bowler" or "All-Rounder" role to enable</span>
                    </div>
                  )}
                  {fieldErrors.bowlingStyle && (
                    <div className="invalid-feedback">{fieldErrors.bowlingStyle}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Jersey Size
                  </label>
                  <select
                    className={`form-select ${fieldErrors.jerseySize ? 'is-invalid' : ''}`}
                    value={registerForm.jerseySize}
                    onChange={(event) => handleRegisterChange('jerseySize', event.target.value)}
                  >
                    <option value="">Select jersey size (optional)</option>
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                    <option value="XXXL">XXXL</option>
                  </select>
                  {fieldErrors.jerseySize && (
                    <div className="invalid-feedback">{fieldErrors.jerseySize}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    className={`form-control ${fieldErrors.password ? 'is-invalid' : ''}`}
                    value={registerForm.password}
                    onChange={(event) => handleRegisterChange('password', event.target.value)}
                  />
                  {fieldErrors.password && (
                    <div className="invalid-feedback">{fieldErrors.password}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Confirm Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    className={`form-control ${fieldErrors.confirmPassword ? 'is-invalid' : ''}`}
                    value={registerForm.confirmPassword}
                    onChange={(event) => handleRegisterChange('confirmPassword', event.target.value)}
                  />
                  {fieldErrors.confirmPassword && (
                    <div className="invalid-feedback">{fieldErrors.confirmPassword}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    NID (National ID)
                  </label>
                  <input
                    type="text"
                    className={`form-control ${fieldErrors.nid ? 'is-invalid' : ''}`}
                    value={registerForm.nid}
                    onChange={(event) => handleRegisterChange('nid', event.target.value)}
                    placeholder="Enter your NID number"
                  />
                  {fieldErrors.nid && <div className="invalid-feedback">{fieldErrors.nid}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Profile Photo <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    className={`form-control ${fieldErrors.profilePhoto ? 'is-invalid' : ''}`}
                    onChange={handleFileChange}
                  />
                  <div className="form-text">Accepted formats: JPEG, JPG, PNG, GIF, WEBP (Max 5MB)</div>
                  {registerForm.profilePhoto && (
                    <div className="mt-2">
                      <small className="text-success">Selected: {registerForm.profilePhoto.name}</small>
                    </div>
                  )}
                  {fieldErrors.profilePhoto && (
                    <div className="invalid-feedback">{fieldErrors.profilePhoto}</div>
                  )}
                </div>
                <div className="col-12">
                  {registerError && <div className="alert alert-danger">{registerError}</div>}
                  <button type="submit" className="btn btn-arena w-100" disabled={registerLoading}>
                    {registerLoading ? 'Submitting...' : 'Register'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
